/**
 * Website CMS pages: list, new page, start from the live page (cms-source seed), and the schema-driven section editor
 * with drafts (optimistic baseVersion), publish, unpublish, version history and rollback.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { CMS_LOCALES, type CmsLocale, type CmsPageDoc, type CmsPageView, type CmsSection } from '@qm/contracts/cms';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, Field, Loadable, Notice, Screen, When, type Tone } from '../kit/ui';
import { cmsSourceClient, type CmsPageDetail } from '../api';
import {
  CMS_PATH_RE, SECTION_TYPES, VALUE_KINDS, addField, addSection, canEditCms, canViewCms, clone, currentWebsiteOrigin, duplicateSection, editSectionField,
  h1Count, isLongText, isMediaField, moveItem, moveSection, newPageDoc, nextListItem, pendingImports, removeSection, removeSectionField, sourceDoc,
  type CmsSourceEntry, type Json, type JsonObject, type JsonPath, type SectionType, type ValueKind,
} from '../lib/cms-logic';
import { useAdminApi } from './layout';
import { ClientError, CmsError, ConfirmButton, MediaLibContext, MediaPicker, MediaThumb, useMediaLib } from './cms-shared';

export const PAGE_STATUS_TONE: Record<CmsPageView['status'], Tone> = { draft: 'warn', published: 'good', unpublished: 'neutral' };

// ================================================================== pages list
export function CmsPagesPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const pages = useResource('cms:pages', api.cmsPages);
  const [showSource, setShowSource] = useState(false);
  if (!canViewCms(roles)) return <Screen title={t('portal.admin.cms.pages.title')}><Notice testId="cms-no-access">{t('portal.admin.cms.noAccess')}</Notice></Screen>;
  const editable = canEditCms(roles);
  return (
    <Screen title={t('portal.admin.cms.pages.title')} subtitle={t('portal.admin.cms.pages.subtitle')} testId="cms-pages"
      actions={editable ? <Button onClick={() => setShowSource((v) => !v)} data-testid="start-from-live">{t('portal.admin.cms.pages.fromLive')}</Button> : null}>
      {editable ? <NewPageForm /> : <Notice testId="cms-read-only">{t('portal.admin.cms.readOnly')}</Notice>}
      {editable && showSource ? <SourcePicker existing={pages.status === 'ready' ? pages.data : []} /> : null}
      <Loadable resource={pages}>
        {(rows) => <DataTable rows={rows} rowKey={(p) => p.id} empty={t('portal.admin.cms.pages.empty')} caption={t('portal.admin.cms.pages.title')} columns={[
          { key: 'p', header: t('portal.admin.cms.pages.path'), render: (p) => <Link to={`/cms/pages/${encodeURIComponent(p.id)}`}><code>{p.path}</code></Link> },
          { key: 'l', header: t('portal.admin.cms.pages.lang'), render: (p) => t(`portal.admin.cms.lang.${p.lang}`) },
          { key: 's', header: t('portal.admin.status'), render: (p) => <Badge tone={PAGE_STATUS_TONE[p.status]} testId={`page-status-${p.status}`}>{t(`portal.admin.cms.status.${p.status}`)}</Badge> },
          { key: 'v', header: t('portal.admin.cms.pages.latest'), render: (p) => `v${p.latestVersion}` },
          { key: 'pv', header: t('portal.admin.cms.pages.published'), render: (p) => (p.publishedVersion ? `v${p.publishedVersion}` : '-') },
          { key: 'u', header: t('portal.admin.cms.pages.updated'), narrow: 'hide', render: (p) => <When iso={p.updatedAt} /> },
        ]} />}
      </Loadable>
    </Screen>
  );
}

function NewPageForm() {
  const { t } = useSurface();
  const api = useAdminApi();
  const navigate = useNavigate();
  const [path, setPath] = useState('/');
  const [lang, setLang] = useState<CmsLocale>('en');
  const [title, setTitle] = useState('');
  const create = useAction(api.cmsCreatePage);
  const valid = CMS_PATH_RE.test(path) && title.trim().length > 0;
  return (
    <Panel data-testid="new-page">
      <Heading level={2}>{t('portal.admin.cms.pages.new')}</Heading>
      <form className="qx-toolbar" onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        const p = await create.run({ path, lang, doc: newPageDoc(title.trim()) });
        if (p) navigate(`/cms/pages/${encodeURIComponent(p.id)}`);
      }}>
        <Field label={t('portal.admin.cms.pages.path')} hint={t('portal.admin.cms.pages.pathHint')} error={path && !CMS_PATH_RE.test(path) ? t('portal.admin.cms.pages.pathInvalid') : null}>
          {(id) => <input id={id} name="path" value={path} onChange={(e) => setPath(e.target.value.trim().toLowerCase())} />}
        </Field>
        <Field label={t('portal.admin.cms.pages.lang')}>
          {(id) => <select id={id} name="lang" value={lang} onChange={(e) => setLang(e.target.value as CmsLocale)}>{CMS_LOCALES.map((l) => <option key={l} value={l}>{t(`portal.admin.cms.lang.${l}`)}</option>)}</select>}
        </Field>
        <Field label={t('portal.admin.cms.doc.title')}>{(id) => <input id={id} name="title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!valid || create.pending}>{t('portal.admin.cms.pages.create')}</Button>
      </form>
      <CmsError error={create.error} />
    </Panel>
  );
}

/** Seed a CMS page from the live site's cms-source export. Locked (code-managed) pages are greyed out. */
export function SourcePicker({ existing }: { existing: readonly CmsPageView[] }) {
  const { t, locale } = useSurface();
  const api = useAdminApi();
  const navigate = useNavigate();
  const origin = currentWebsiteOrigin();
  const src = useMemo(() => cmsSourceClient(origin), [origin]);
  const index = useResource('cms:source:index', src.index);
  const start = useAction(async (entry: CmsSourceEntry, lang: CmsLocale) => {
    const doc = sourceDoc(await src.page(entry.slug), lang);
    if (!doc) throw new Error('cms-source missing');
    return api.cmsCreatePage({ path: entry.path, lang, doc, note: t('portal.admin.cms.pages.importNote') });
  });
  const [imported, setImported] = useState<{ done: number; failed: string[] } | null>(null);
  // One page at a time (server-side validation per page); a failure is listed and the rest still import.
  const importAll = useAction(async (todo: { entry: CmsSourceEntry; lang: CmsLocale }[]) => {
    let done = 0;
    const failed: string[] = [];
    for (const { entry, lang } of todo) {
      try {
        const doc = sourceDoc(await src.page(entry.slug), lang);
        if (!doc) throw new Error('cms-source missing');
        await api.cmsCreatePage({ path: entry.path, lang, doc, note: t('portal.admin.cms.pages.importNote') });
        done += 1;
      } catch {
        failed.push(`${entry.path} (${lang})`);
      }
    }
    setImported({ done, failed });
    return done;
  });
  const exists = (path: string, lang: CmsLocale) => existing.find((p) => p.path === path && p.lang === lang);
  return (
    <Panel data-testid="source-picker">
      <Heading level={2}>{t('portal.admin.cms.pages.fromLive')}</Heading>
      <Text tone="muted">{t('portal.admin.cms.pages.fromLiveNote', { origin })}</Text>
      <Loadable resource={index}>
        {(rows) => (<>
          {(() => {
            const todo = imported ? [] : pendingImports(rows, existing);
            return todo.length ? (
              <Button variant='primary' disabled={importAll.pending || start.pending} data-testid='import-all' onClick={() => void importAll.run(todo)}>
                {t('portal.admin.cms.pages.importAll', { count: String(todo.length) })}
              </Button>
            ) : null;
          })()}
          {imported ? (
            <Notice testId='import-result'>
              {t('portal.admin.cms.pages.importDone', { count: String(imported.done) })}
              {imported.failed.length ? ` ${t('portal.admin.cms.pages.importFailed', { list: imported.failed.join(', ') })}` : null}
              {' '}<Button onClick={() => navigate(0)} data-testid='import-reload'>{t('portal.admin.cms.pages.importReload')}</Button>
            </Notice>
          ) : null}
          <ul className='qx-list qx-source'>
            {rows.map((e) => (
              <li key={e.slug} className={e.locked ? 'qx-locked' : undefined} data-slug={e.slug} data-locked={e.locked ? 'true' : undefined} aria-disabled={e.locked || undefined}>
                <code>{e.path}</code> <span>{(locale === 'ar' ? e.title.ar : e.title.en) ?? e.title.en ?? e.slug}</span>
                {e.locked ? <Badge testId={`locked-${e.slug}`}>{t('portal.admin.cms.pages.locked')}</Badge> : null}
                {CMS_LOCALES.filter((l) => l === 'en' || e.hasAr).map((l) => {
                  const have = exists(e.path, l);
                  if (have) return <Link key={l} to={`/cms/pages/${encodeURIComponent(have.id)}`}>{t('portal.admin.cms.pages.openExisting', { lang: t(`portal.admin.cms.lang.${l}`) })}</Link>;
                  return (
                    <Button key={l} disabled={e.locked || start.pending} data-testid={`start-${e.slug}-${l}`}
                      onClick={async () => { const p = await start.run(e, l); if (p) navigate(`/cms/pages/${encodeURIComponent(p.id)}`); }}>
                      {t('portal.admin.cms.pages.startIn', { lang: t(`portal.admin.cms.lang.${l}`) })}
                    </Button>
                  );
                })}
              </li>
            ))}
          </ul>
        </>)}
      </Loadable>
      <CmsError error={start.error} />
    </Panel>
  );
}

// ================================================================== page editor
export function CmsPageEditorPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const { id = '' } = useParams();
  const page = useResource(`cms:page:${id}`, () => api.cmsPage(id));
  const media = useResource('cms:media:', () => api.cmsMedia());
  const origin = currentWebsiteOrigin();
  const lib = useMemo(() => ({ items: media.status === 'ready' ? media.data : [], origin, loading: media.status === 'loading' }), [media.status, media.data, origin]);
  if (!canViewCms(roles)) return <Screen title={t('portal.admin.cms.editor.title')}><Notice testId="cms-no-access">{t('portal.admin.cms.noAccess')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.cms.editor.title')} testId="cms-editor" actions={<Link to="/cms/pages">{t('portal.admin.cms.editor.back')}</Link>}>
      <MediaLibContext.Provider value={lib}>
        <Loadable resource={page}>
          {(p) => <PageEditor key={`${p.id}:${p.latestVersion}:${p.status}:${p.publishedVersion ?? 0}`} page={p} onReload={page.reload} />}
        </Loadable>
      </MediaLibContext.Provider>
    </Screen>
  );
}

export function PageEditor({ page, onReload }: { page: CmsPageDetail; onReload: () => void }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const editable = canEditCms(roles);
  const [doc, setDoc] = useState<CmsPageDoc>(() => clone(page.doc));
  const [note, setNote] = useState('');
  const [addType, setAddType] = useState<SectionType>('rich-text');
  const save = useAction(() => api.cmsSaveDraft(page.id, doc, page.latestVersion, note));
  const publish = useAction(() => api.cmsPublish(page.id, page.latestVersion));
  const unpublish = useAction(() => api.cmsUnpublish(page.id));
  const rollback = useAction((v: number) => api.cmsRollback(page.id, v));
  const view = useAction((v: number) => api.cmsVersion(page.id, v));
  const dirty = JSON.stringify(doc) !== JSON.stringify(page.doc);
  const heroes = h1Count(doc);
  const done = async (p: Promise<unknown>) => { if (await p) onReload(); };
  const setSections = (f: (s: CmsSection[]) => CmsSection[]) => setDoc((d) => ({ ...d, sections: f(d.sections) }));
  const error = save.error ?? publish.error ?? unpublish.error ?? rollback.error ?? view.error;
  return (
    <div className="qx-editor" data-testid="page-editor" data-page={page.id}>
      <Panel>
        <div className="qx-toolbar">
          <code data-testid="page-path">{page.path}</code>
          <Badge>{t(`portal.admin.cms.lang.${page.lang}`)}</Badge>
          <Badge tone={PAGE_STATUS_TONE[page.status]} testId={`page-status-${page.status}`}>{t(`portal.admin.cms.status.${page.status}`)}</Badge>
          <Text tone="muted">{t('portal.admin.cms.editor.versions', { latest: page.latestVersion, published: page.publishedVersion ?? '-' })}</Text>
        </div>
        {!editable ? <Notice testId="cms-read-only">{t('portal.admin.cms.readOnly')}</Notice> : null}
      </Panel>

      <Panel data-testid="page-settings">
        <Heading level={2}>{t('portal.admin.cms.doc.settings')}</Heading>
        <Field label={t('portal.admin.cms.doc.title')}>{(id) => <input id={id} name="doc-title" value={doc.title} maxLength={200} disabled={!editable} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />}</Field>
        <Field label={t('portal.admin.cms.doc.description')}>{(id) => <textarea id={id} name="doc-description" value={doc.description} maxLength={400} disabled={!editable} onChange={(e) => setDoc({ ...doc, description: e.target.value })} />}</Field>
        <Checkbox name="doc-noindex" label={t('portal.admin.cms.doc.noindex')} checked={doc.noindex === true} disabled={!editable} onChange={(v) => setDoc({ ...doc, noindex: v })} />
        <Field label={t('portal.admin.cms.doc.ogTitle')} hint={t('portal.admin.cms.doc.ogHint')}>
          {(id) => <input id={id} name="og-title" value={doc.og?.title ?? ''} maxLength={200} disabled={!editable} onChange={(e) => setDoc({ ...doc, og: ogPatch(doc.og, 'title', e.target.value) })} />}
        </Field>
        <Field label={t('portal.admin.cms.doc.ogDescription')}>
          {(id) => <textarea id={id} name="og-description" value={doc.og?.description ?? ''} maxLength={400} disabled={!editable} onChange={(e) => setDoc({ ...doc, og: ogPatch(doc.og, 'description', e.target.value) })} />}
        </Field>
      </Panel>

      <Panel data-testid="sections">
        <Heading level={2}>{t('portal.admin.cms.sections.title', { n: doc.sections.length })}</Heading>
        {heroes !== 1 ? <Notice tone="warn" testId="h1-warning">{t('portal.admin.cms.err.one_h1', { found: heroes })}</Notice> : null}
        {doc.sections.map((s, i) => (
          <SectionCard key={i} section={s} index={i} count={doc.sections.length} editable={editable}
            onMove={(dir) => setSections((xs) => moveSection(xs, i, dir))}
            onDuplicate={() => setSections((xs) => duplicateSection(xs, i))}
            onRemove={() => setSections((xs) => removeSection(xs, i))}
            onSet={(path, v) => setSections((xs) => editSectionField(xs, i, path, v))}
            onUnset={(path) => setSections((xs) => removeSectionField(xs, i, path))} />
        ))}
        {editable ? (
          <div className="qx-toolbar" data-testid="add-section">
            <Field label={t('portal.admin.cms.sections.type')}>
              {(id) => <select id={id} name="section-type" value={addType} onChange={(e) => setAddType(e.target.value as SectionType)}>{SECTION_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select>}
            </Field>
            <Button onClick={() => setSections((xs) => addSection(xs, addType))} disabled={doc.sections.length >= 60}>{t('portal.admin.cms.sections.add')}</Button>
          </div>
        ) : null}
      </Panel>

      {editable ? (
        <Panel data-testid="page-actions">
          <Field label={t('portal.admin.cms.editor.note')}>{(id) => <input id={id} name="note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}</Field>
          <div className="qx-toolbar">
            <Button variant="primary" data-testid="save-draft" disabled={!dirty || save.pending || !doc.title.trim() || doc.sections.length === 0} onClick={() => void done(save.run())}>{t('portal.admin.cms.editor.save')}</Button>
            <Button data-testid="publish" disabled={dirty || publish.pending || page.publishedVersion === page.latestVersion} onClick={() => void done(publish.run())}>{t('portal.admin.cms.editor.publish', { v: page.latestVersion })}</Button>
            {page.status === 'published' ? <ConfirmButton testId="unpublish" label={t('portal.admin.cms.editor.unpublish')} confirmLabel={t('portal.admin.cms.editor.unpublishConfirm')} question={t('portal.admin.cms.editor.unpublishQuestion')} onConfirm={() => void done(unpublish.run())} disabled={unpublish.pending} /> : null}
          </div>
          {dirty ? <Text tone="muted" data-testid="dirty">{t('portal.admin.cms.editor.dirty')}</Text> : null}
          <CmsError error={error} onReload={onReload} testId="editor-error" />
        </Panel>
      ) : <CmsError error={view.error} />}

      <Panel data-testid="history">
        <Heading level={2}>{t('portal.admin.cms.history.title')}</Heading>
        <DataTable rows={page.versions} rowKey={(v) => String(v.version)} empty={t('portal.kit.empty')} columns={[
          { key: 'v', header: t('portal.admin.cms.history.version'), render: (v) => <span>v{v.version} {v.version === page.publishedVersion ? <Badge tone="good">{t('portal.admin.cms.status.published')}</Badge> : null}</span> },
          { key: 'n', header: t('portal.admin.cms.editor.note'), render: (v) => v.note ?? '' },
          { key: 'b', header: t('portal.admin.cms.history.by'), narrow: 'hide', render: (v) => v.createdBy },
          { key: 'a', header: t('portal.admin.cms.history.at'), render: (v) => <When iso={v.createdAt} /> },
          { key: 'x', header: t('portal.admin.cms.history.actions'), render: (v) => (
            <span className="qx-toolbar">
              <Button onClick={() => void view.run(v.version)} data-testid={`view-${v.version}`}>{t('portal.admin.cms.history.view')}</Button>
              {editable && page.publishedVersion !== null && v.version !== page.publishedVersion ? (
                <ConfirmButton testId={`rollback-${v.version}`} label={t('portal.admin.cms.history.rollback')} confirmLabel={t('portal.admin.cms.history.rollbackConfirm')}
                  question={t('portal.admin.cms.history.rollbackQuestion', { v: v.version })} onConfirm={() => void done(rollback.run(v.version))} disabled={rollback.pending} />
              ) : null}
            </span>
          ) },
        ]} />
        {editable && page.publishedVersion === null ? <Text tone="muted">{t('portal.admin.cms.history.rollbackNeedsPublished')}</Text> : null}
        {view.result ? (
          <div data-testid="version-view">
            <Heading level={3}>{t('portal.admin.cms.history.viewing', { v: view.result.version })}</Heading>
            <pre className="qx-pre">{JSON.stringify(view.result.doc, null, 2)}</pre>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

function ogPatch(og: CmsPageDoc['og'], k: 'title' | 'description', v: string): CmsPageDoc['og'] {
  const next = { ...(og ?? {}), [k]: v };
  if (!v) delete next[k];
  return Object.keys(next).length ? next : undefined;
}

// ================================================================== section editor (generic, recursive)
interface EditOps { onSet: (path: JsonPath, v: Json) => void; onUnset: (path: JsonPath) => void; editable: boolean }

export function SectionCard({ section, index, count, editable, onMove, onDuplicate, onRemove, onSet, onUnset }: {
  section: CmsSection; index: number; count: number; editable: boolean; onMove: (dir: -1 | 1) => void; onDuplicate: () => void; onRemove: () => void;
} & Omit<EditOps, 'editable'>) {
  const { t } = useSurface();
  const obj = section as unknown as JsonObject;
  return (
    <section className="qx-section" data-testid="section" data-type={section.type} data-index={index} aria-label={t('portal.admin.cms.sections.label', { n: index + 1, type: section.type })}>
      <header className="qx-section__head">
        <span><Badge tone="accent">{section.type}</Badge> <Text tone="muted">#{index + 1}</Text></span>
        {editable ? (
          <span className="qx-toolbar">
            <Button onClick={() => onMove(-1)} disabled={index === 0} aria-label={t('portal.admin.cms.moveUp')}>{t('portal.admin.cms.up')}</Button>
            <Button onClick={() => onMove(1)} disabled={index === count - 1} aria-label={t('portal.admin.cms.moveDown')}>{t('portal.admin.cms.down')}</Button>
            <Button onClick={onDuplicate} data-testid="section-duplicate">{t('portal.admin.cms.sections.duplicate')}</Button>
            <Button onClick={onRemove} data-danger="true" data-testid="section-remove" disabled={count <= 1}>{t('portal.admin.remove')}</Button>
          </span>
        ) : null}
      </header>
      <ObjectEditor obj={obj} path={[]} reserved={['type']} ops={{ onSet, onUnset, editable }} depth={0} />
    </section>
  );
}

const MAX_DEPTH = 8;

function ObjectEditor({ obj, path, reserved, ops, depth }: { obj: JsonObject; path: JsonPath; reserved?: readonly string[]; ops: EditOps; depth: number }) {
  const { t } = useSurface();
  const keys = Object.keys(obj).filter((k) => !reserved?.includes(k));
  return (
    <div className="qx-tree" data-depth={depth}>
      {keys.map((k) => (
        <div key={k} className="qx-tree__row" data-field={[...path, k].join('.')}>
          <ValueEditor value={obj[k]!} fieldKey={k} path={[...path, k]} ops={ops} depth={depth} />
          {ops.editable ? <Button onClick={() => ops.onUnset([...path, k])} aria-label={t('portal.admin.cms.fields.removeNamed', { key: k })} data-testid="field-remove">{t('portal.admin.remove')}</Button> : null}
        </div>
      ))}
      {ops.editable && depth < MAX_DEPTH ? <AddFieldForm obj={obj} reserved={reserved} onAdd={(k, v) => ops.onSet([...path, k], v)} /> : null}
    </div>
  );
}

function AddFieldForm({ obj, reserved, onAdd }: { obj: JsonObject; reserved?: readonly string[]; onAdd: (key: string, v: Json) => void }) {
  const { t } = useSurface();
  const [key, setKey] = useState('');
  const [kind, setKind] = useState<ValueKind>('text');
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="qx-toolbar qx-addfield" data-testid="add-field">
      <Field label={t('portal.admin.cms.fields.key')} hint={t('portal.admin.cms.fields.keyHint')}>{(id) => <input id={id} name="field-key" value={key} maxLength={64} onChange={(e) => { setKey(e.target.value); setErr(null); }} />}</Field>
      <Field label={t('portal.admin.cms.fields.kind')}>
        {(id) => <select id={id} name="field-kind" value={kind} onChange={(e) => setKind(e.target.value as ValueKind)}>{VALUE_KINDS.map((k) => <option key={k} value={k}>{t(`portal.admin.cms.kind.${k}`)}</option>)}</select>}
      </Field>
      <Button disabled={!key.trim()} onClick={() => {
        const r = addField(obj, key, kind, { reserved });
        if (!r.ok) { setErr(r.reason); return; }
        const k = key.trim();
        onAdd(k, r.value[k]!);
        setKey('');
      }}>{t('portal.admin.cms.fields.add')}</Button>
      <ClientError reason={err} />
    </div>
  );
}

function ValueEditor({ value, fieldKey, path, ops, depth }: { value: Json; fieldKey: string | number; path: JsonPath; ops: EditOps; depth: number }) {
  const { t } = useSurface();
  const label = typeof fieldKey === 'number' ? t('portal.admin.cms.fields.item', { n: fieldKey + 1 }) : fieldKey;
  if (typeof fieldKey === 'string' && isMediaField(fieldKey) && (typeof value === 'string' || value === null)) {
    return <MediaField label={label} value={value ?? ''} onlyImages={fieldKey === 'posterId'} editable={ops.editable} onChange={(v) => ops.onSet(path, v)} />;
  }
  if (typeof value === 'string' || value === null) {
    const s = value ?? '';
    return (
      <Field label={label}>
        {(id) => (isLongText(s, fieldKey)
          ? <textarea id={id} name={path.join('.')} value={s} disabled={!ops.editable} onChange={(e) => ops.onSet(path, e.target.value)} />
          : <input id={id} name={path.join('.')} value={s} disabled={!ops.editable} onChange={(e) => ops.onSet(path, e.target.value)} />)}
      </Field>
    );
  }
  if (typeof value === 'number') {
    return <Field label={label}>{(id) => <input id={id} type="number" name={path.join('.')} value={value} disabled={!ops.editable} onChange={(e) => ops.onSet(path, e.target.value === '' ? 0 : Number(e.target.value))} />}</Field>;
  }
  if (typeof value === 'boolean') {
    return <Checkbox name={path.join('.')} label={label} checked={value} disabled={!ops.editable} onChange={(v) => ops.onSet(path, v)} />;
  }
  if (depth >= MAX_DEPTH) return <Text tone="muted">{label}: {t('portal.admin.cms.fields.tooDeep')}</Text>;
  if (Array.isArray(value)) {
    return (
      <fieldset className="qx-group" data-kind="list">
        <legend>{label}</legend>
        <ListEditor list={value} path={path} ops={ops} depth={depth + 1} />
      </fieldset>
    );
  }
  return (
    <fieldset className="qx-group" data-kind="group">
      <legend>{label}</legend>
      <ObjectEditor obj={value} path={path} ops={ops} depth={depth + 1} />
    </fieldset>
  );
}

function ListEditor({ list, path, ops, depth }: { list: Json[]; path: JsonPath; ops: EditOps; depth: number }) {
  const { t } = useSurface();
  const [kind, setKind] = useState<ValueKind>('text');
  return (
    <div className="qx-tree" data-depth={depth}>
      {list.length === 0 ? <Text tone="muted">{t('portal.admin.cms.fields.emptyList')}</Text> : null}
      {list.map((item, i) => (
        <div key={i} className="qx-tree__row" data-item={i}>
          <ValueEditor value={item} fieldKey={i} path={[...path, i]} ops={ops} depth={depth} />
          {ops.editable ? (
            <span className="qx-toolbar">
              <Button onClick={() => ops.onSet(path, moveItem(list, i, -1))} disabled={i === 0} aria-label={t('portal.admin.cms.moveUp')}>{t('portal.admin.cms.up')}</Button>
              <Button onClick={() => ops.onSet(path, moveItem(list, i, 1))} disabled={i === list.length - 1} aria-label={t('portal.admin.cms.moveDown')}>{t('portal.admin.cms.down')}</Button>
              <Button onClick={() => ops.onUnset([...path, i])} data-testid="item-remove">{t('portal.admin.remove')}</Button>
            </span>
          ) : null}
        </div>
      ))}
      {ops.editable ? (
        <div className="qx-toolbar">
          {list.length === 0 ? (
            <Field label={t('portal.admin.cms.fields.kind')}>
              {(id) => <select id={id} value={kind} onChange={(e) => setKind(e.target.value as ValueKind)}>{VALUE_KINDS.map((k) => <option key={k} value={k}>{t(`portal.admin.cms.kind.${k}`)}</option>)}</select>}
            </Field>
          ) : null}
          <Button onClick={() => ops.onSet([...path, list.length], nextListItem(list, kind))} disabled={list.length >= 200} data-testid="item-add">{t('portal.admin.cms.fields.addItem')}</Button>
        </div>
      ) : null}
    </div>
  );
}

function MediaField({ label, value, onlyImages, editable, onChange }: { label: string; value: string; onlyImages: boolean; editable: boolean; onChange: (v: string) => void }) {
  const { t } = useSurface();
  const lib = useMediaLib();
  const [open, setOpen] = useState(false);
  const media = lib.items.find((m) => m.id === value) ?? null;
  return (
    <div className="qx-mediafield" data-testid="media-field">
      <Field label={label} hint={value && !media && !lib.loading ? t('portal.admin.cms.media.notInLibrary') : undefined}>
        {(id) => <input id={id} value={value} disabled={!editable} onChange={(e) => onChange(e.target.value.trim())} />}
      </Field>
      <MediaThumb media={media} id={value} origin={lib.origin} />
      {editable ? <Button onClick={() => setOpen((v) => !v)} data-testid="media-choose">{t('portal.admin.cms.media.choose')}</Button> : null}
      {editable && value ? <Button onClick={() => onChange('')}>{t('portal.admin.cms.media.clear')}</Button> : null}
      {open ? <MediaPicker onlyImages={onlyImages} selected={value ? [value] : []} onPick={(id) => { onChange(id); setOpen(false); }} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
